<script setup lang="ts">
import { computed } from 'vue';
import { resolveStoryblokStringOption } from '@/utils/storyblokOption';

interface HeroBlok {
    _uid: string;
    tagline?: string;
    headline: string;
    subline: string;
    cta_text: string;
    cta_link: { cached_url: string; url: string };
    background_image: { filename: string; alt: string };
    overlay_opacity: string;
    text_align?: 'left' | 'center';
    tagline_style?: 'inline' | 'badge';
    mobile_image_focus?: 'left' | 'center' | 'right' | string | { value: string };
}

const props = defineProps<{ blok: HeroBlok }>();

const isLeft = computed(() => props.blok.text_align === 'left');
const isBadge = computed(() => props.blok.tagline_style === 'badge');

/** Storyblok assets support `/m/…` transforms; external URLs must be used as-is. */
const backgroundImageCssUrl = computed(() => {
    const filename = props.blok.background_image?.filename;
    if (!filename) {
        return undefined;
    }
    const isStoryblok = filename.includes('storyblok.com');
    const src = isStoryblok ? `${filename}/m/1920x0` : filename;
    // Unquoted url() is invalid when the URL contains "(" or ")" (e.g. Webflow "...(1).png").
    const escaped = src.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

    return `url("${escaped}")`;
});

const mobileBgFocusClass = computed(() => {
    const f = resolveStoryblokStringOption(props.blok.mobile_image_focus).toLowerCase();
    if (f === 'left') {
        return 'bg-left lg:bg-center';
    }
    if (f === 'right') {
        return 'bg-right lg:bg-center';
    }

    return 'bg-center';
});

const sectionLayoutClass = computed(() =>
    isLeft.value
        ? 'items-stretch justify-stretch md:items-start md:justify-start'
        : 'items-stretch justify-stretch md:items-center md:justify-center',
);

function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

const formattedHeadline = computed(() => escapeHtml(props.blok.headline ?? '').replace(/\n/g, '<br>'));

function resolveLink(link: { cached_url: string; url: string } | undefined): string {
    return link?.cached_url || link?.url || '#';
}
</script>

<template>
    <section
        v-editable="blok"
        class="relative flex min-h-[520px] w-full flex-col bg-cover md:min-h-[500px] xl:min-h-[600px]"
        :class="[mobileBgFocusClass, sectionLayoutClass]"
        :style="{ backgroundImage: backgroundImageCssUrl }"
    >
        <div class="absolute inset-0" :class="blok.overlay_opacity === 'light' ? 'bg-black/20' : 'bg-black/40'" />

        <div
            v-if="blok.tagline && isBadge"
            class="absolute right-4 top-4 z-20 hidden h-[118px] w-[118px] shrink-0 items-center justify-center rounded-full bg-white px-3 text-center shadow-md md:right-8 md:top-8 md:flex xl:h-[132px] xl:w-[132px]"
        >
            <p class="text-sm font-semibold leading-snug text-[#F08E59] md:text-base">
                {{ blok.tagline }}
            </p>
        </div>

        <div
            class="relative z-10 flex flex-1 flex-col justify-between px-4 pb-8 pt-20 md:flex-none md:justify-start md:py-16 md:pt-24 xl:pb-24 xl:pt-36"
            :class="
                isLeft
                    ? 'w-full max-w-5xl text-center md:px-12 md:text-left xl:px-20'
                    : 'mx-auto w-full max-w-2xl text-center'
            "
        >
            <div>
                <p
                    v-if="blok.tagline && !isBadge"
                    class="mb-3 text-base font-normal uppercase tracking-wide text-white/80 xl:mb-4"
                >
                    {{ blok.tagline }}
                </p>
                <h1
                    class="text-3xl font-semibold leading-[110%] text-white md:text-4xl xl:text-5xl xl:leading-[110%]"
                    v-html="formattedHeadline"
                />
                <p
                    v-if="blok.subline"
                    class="mt-5 text-lg font-normal text-white/90 xl:mt-6 xl:text-xl"
                    :class="isLeft ? 'mx-auto max-w-2xl md:mx-0' : 'mx-auto max-w-md xl:max-w-lg'"
                >
                    {{ blok.subline }}
                </p>
            </div>
            <a
                v-if="blok.cta_text"
                :href="resolveLink(blok.cta_link)"
                class="inline-flex w-full items-center justify-center rounded-lg bg-[#F08E59] px-8 py-4 text-lg font-semibold text-white shadow-orange-button transition md:mt-8 md:w-fit md:max-w-none xl:mt-12"
                :class="
                    isLeft
                        ? 'mx-auto self-center md:mx-0 md:self-start'
                        : 'mx-auto self-center md:self-center'
                "
            >
                {{ blok.cta_text }}
            </a>
        </div>
    </section>
</template>
